/**
 * Frameium — poster results inside the store's own search.
 *
 * Images from the Pinterest API are appended to the native results as ordinary
 * product cards, titled with the customer's search term and priced from the
 * "Find your poster" product. Two surfaces, each using its own card markup so the
 * cards are indistinguishable from real ones:
 *
 *   Predictive dropdown — the theme's resource-card, appended to the products
 *     list. When the search found nothing the list doesn't exist, so we build it
 *     and drop the "no results" line, which is no longer true.
 *
 *   Search page — a plain card in a product-grid cell. The grid is always there,
 *     even with zero results, because the theme falls back to the empty-state
 *     collection.
 *
 * The dropdown's results container is replaced by morph() on every keystroke, so
 * injection is driven by a MutationObserver and repeats after each render.
 *
 * Cards link to the product carrying the signed image params (p + k), so the
 * picked image is already applied on arrival — see blocks/pinterest-image-search.
 */
(function () {
  'use strict';

  var cfg = document.querySelector('[data-fm-ps]');
  if (!cfg) return;

  var API = (cfg.getAttribute('data-api') || '').replace(/\/+$/, '');
  var PRODUCT = cfg.getAttribute('data-product') || '';
  var VARIANT = cfg.getAttribute('data-variant') || '';
  var PRICE = cfg.getAttribute('data-price') || '';
  var COMPARE = cfg.getAttribute('data-compare') || '';
  var DIALOG_COUNT = parseInt(cfg.getAttribute('data-dialog-count'), 10) || 6;
  var GRID_COUNT = parseInt(cfg.getAttribute('data-grid-count'), 10) || 12;
  var SEARCH_COUNT = parseInt(cfg.getAttribute('data-search-count'), 10) || 0;
  var PRODUCTS_TITLE = cfg.getAttribute('data-products-title') || 'Products';

  if (!API || !PRODUCT) return;

  // Two, not three: real poster searches are this short — "f1", "gt", "ac" — and a
  // three-character floor silently skipped the poster results for all of them.
  var MIN_CHARS = 2;
  var TYPING_DELAY = 450;
  var memo = {};

  /* ────────────────────────────── helpers ────────────────────────────── */

  function paramOf(url, name) {
    var m = new RegExp('[?&]' + name + '=([^&]*)').exec(url || '');
    return m ? decodeURIComponent(m[1]) : '';
  }

  function withQuery(base, params) {
    var parts = [];
    for (var k in params) {
      if (params[k] === '' || params[k] == null) continue;
      parts.push(k + '=' + encodeURIComponent(params[k]));
    }
    return base + (base.indexOf('?') === -1 ? '?' : '&') + parts.join('&');
  }

  // The query goes out exactly as typed — the API appends its poster suffix
  // server-side, so every surface lands on the same result list without any
  // client keeping its own copy of the word.

  // fpp_s carries the sent string only when it differs from the typed one; with
  // the suffix server-side the two always match, so it stays empty. Kept for
  // pinLink's signature and for old links that still carry it.
  function pinLink(query, sent, im) {
    return withQuery(PRODUCT, {
      // Without this the product opens on its first variant, the smallest size.
      variant: VARIANT,
      fpp_q: query,
      fpp_s: sent === query ? '' : sent,
      fpp_pin: im.id || '',
      fpp_p: im.ref || paramOf(im.image, 'p'),
      fpp_k: paramOf(im.image, 'k'),
      fpp_w: im.width || '',
      fpp_h: im.height || '',
      fpp_t: (im.title || '').slice(0, 90)
    });
  }

  // The framed render, so a card reads as a product rather than a stray picture.
  // Framing only exists at mid size, but the signature covers the image id and not
  // the size, so the thumb of the same framed render is safe and much lighter.
  function framedThumb(im) {
    if (im.framed) return im.framed.replace('s=mid', 's=thumb');
    return im.thumb || im.image;
  }

  // One page. `bookmark` is the API's cursor — omit it for the first page, pass the
  // one the previous page returned for the next. A page with no bookmark is the end.
  function searchPage(sent, raw, bookmark) {
    var url = API + '/api/pin/search?query=' + encodeURIComponent(sent);
    // Kept for the rare case sent and typed ever differ again — analytics
    // records the typed words.
    if (raw && raw !== sent) url += '&raw=' + encodeURIComponent(raw);
    if (bookmark) url += '&bookmark=' + encodeURIComponent(bookmark);

    return fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('search ' + r.status);
        return r.json();
      })
      .then(function (data) {
        return { list: data.results || [], bookmark: data.bookmark || null };
      });
  }

  // First page only, cached for the session. The cursor is cached with it — without
  // it, coming back to a page would resume paging from the start and repeat itself.
  function fetchPins(sent, raw) {
    if (memo[sent]) return Promise.resolve(memo[sent]);

    try {
      var stored = sessionStorage.getItem('fm-ps:' + sent);
      if (stored) {
        var parsed = JSON.parse(stored);
        // Entries written before paging existed are a bare array.
        memo[sent] = Array.isArray(parsed) ? { list: parsed, bookmark: null } : parsed;
        return Promise.resolve(memo[sent]);
      }
    } catch (e) {
      /* private mode: just fetch */
    }

    return searchPage(sent, raw, null).then(function (page) {
      memo[sent] = page;
      try {
        sessionStorage.setItem('fm-ps:' + sent, JSON.stringify(page));
      } catch (e) {
        /* quota — not worth failing over */
      }
      return page;
    });
  }

  function priceMarkup() {
    var html = '';
    if (COMPARE) html += '<span class="compare-at-price">' + COMPARE + '</span> ';
    if (PRICE) html += '<span class="price">' + PRICE + '</span>';
    return html;
  }

  // The framed render is generated on demand, so a card can sit empty for seconds
  // after its markup exists. Keep the shimmer on the media box until the image
  // actually paints rather than only until the search responds.
  function shimmerUntilLoaded(card) {
    var media = card.querySelector('[data-fm-ps-media]');
    var img = card.querySelector('img');
    if (!media || !img) return;
    media.classList.add('fm-ps-loading');
    var done = function () {
      media.classList.remove('fm-ps-loading');
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
    };
    if (img.complete && img.naturalWidth) done();
    else {
      img.addEventListener('load', done);
      img.addEventListener('error', done);
    }
  }

  function skeletonCards(kind, count) {
    var out = [];
    for (var i = 0; i < count; i++) {
      var li = document.createElement('li');
      li.setAttribute('data-fm-ps-card', '');
      li.setAttribute('data-fm-ps-skeleton', '');
      if (kind === 'dialog') {
        li.className = 'predictive-search-results__card predictive-search-results__card--product';
        li.innerHTML =
          '<div class="resource-card">' +
          '<div class="resource-card__media fm-ps-loading" style="--resource-card-aspect-ratio: 1 / 1;"></div>' +
          '<div class="resource-card__content">' +
          '<span class="fm-ps-skel-line"></span><span class="fm-ps-skel-line fm-ps-skel-line--short"></span>' +
          '</div></div>';
      } else {
        li.className = 'product-grid__item';
        li.innerHTML =
          '<span class="fm-pcard">' +
          '<span class="fm-pcard__media fm-ps-loading"></span>' +
          '<span class="fm-ps-skel-line"></span><span class="fm-ps-skel-line fm-ps-skel-line--short"></span>' +
          '</span>';
      }
      out.push(li);
    }
    return out;
  }

  /* ─────────────────────── predictive dropdown ─────────────────────── */

  function dropdownCard(im, title, href) {
    var li = document.createElement('li');
    li.className = 'predictive-search-results__card predictive-search-results__card--product';
    li.setAttribute('ref', 'resultsItems[]');
    li.setAttribute('data-fm-ps-card', '');
    li.innerHTML =
      '<div class="resource-card" data-resource-type="product">' +
      '<a class="resource-card__link" href="' + href + '"><span class="visually-hidden">' + title + '</span></a>' +
      // Square, because every framed render is a 736x736 canvas whatever the
      // original's aspect.
      '<div class="resource-card__media" data-fm-ps-media style="--resource-card-aspect-ratio: 1 / 1;">' +
      // fm-ps-img forces object-fit: contain. The theme's resource-card__image is
      // cover, which trims the frame off the edges of the render.
      '<img class="resource-card__image fm-ps-img" src="' + framedThumb(im) + '" alt="" loading="lazy" width="500" height="500">' +
      '</div>' +
      '<div class="resource-card__content">' +
      '<p class="resource-card__title paragraph">' + title + '</p>' +
      '<div>' + priceMarkup() + '</div>' +
      '</div>' +
      '</div>';
    return li;
  }

  function initDialog() {
    var comp = document.querySelector('predictive-search-component');
    if (!comp) return;

    var input = comp.querySelector('input[ref="searchInput"]') || comp.querySelector('.search-input');
    var results = comp.querySelector('[ref="predictiveSearchResults"]');
    if (!input || !results) return;

    var timer = null;
    var token = 0;

    var observer = new MutationObserver(function () {
      schedule();
    });

    function watch() {
      observer.observe(results, { childList: true, subtree: true });
    }

    // Injecting mutates the very container we're watching, so the observer is
    // stopped around the write instead of trying to tell our own edits apart.
    function write(fn) {
      observer.disconnect();
      try {
        fn();
      } finally {
        watch();
      }
    }

    function clearCards() {
      var existing = results.querySelectorAll('[data-fm-ps-card]');
      if (!existing.length) return;
      write(function () {
        existing.forEach(function (n) {
          n.remove();
        });
      });
    }

    // With no matches the theme renders a "no results" line and no products list.
    // Build the list it would have rendered, and drop the line — it isn't true any
    // more once these cards are on screen.
    function productList() {
      // "Recently viewed" is rendered with the same wrapper-products class, so a
      // plain lookup finds it whenever the search matched nothing and appends the
      // cards under that heading — filed as products the customer already looked at.
      var lists = results.querySelectorAll('.predictive-search-results__wrapper-products');
      for (var i = 0; i < lists.length; i++) {
        if (!lists[i].closest('[ref="recentlyViewedWrapper"], .recently-viewed-wrapper')) return lists[i];
      }

      var inner = results.querySelector('[data-search-results]') || results;
      var noResults = inner.querySelector('.predictive-search-results__no-results');
      if (noResults) noResults.remove();

      // Deliberately no id — the theme's empty state already renders an element
      // with id="predictive-search-products" to hold recently-viewed products.
      var box = document.createElement('div');
      box.className = 'predictive-search-results__products';
      box.setAttribute('data-fm-ps-card', '');
      box.innerHTML =
        '<h4 class="predictive-search-results__title">' + PRODUCTS_TITLE + '</h4>' +
        '<ul class="predictive-search-results__list predictive-search-results__wrapper-products list-unstyled"' +
        ' role="listbox" aria-label="' + PRODUCTS_TITLE + '"></ul>';
      // First, not last. With no matches the theme falls back to "Recently viewed",
      // and appending would bury the only results for what they actually typed
      // below the fold of the dropdown.
      inner.insertBefore(box, inner.firstChild);
      return box.querySelector('ul');
    }

    function inject(q, sent, list) {
      write(function () {
        results.querySelectorAll('[data-fm-ps-card]').forEach(function (n) {
          n.remove();
        });
        if (!list.length) return;
        var host = productList();
        list.slice(0, DIALOG_COUNT).forEach(function (im) {
          var card = dropdownCard(im, q, pinLink(q, sent, im));
          host.appendChild(card);
          shimmerUntilLoaded(card);
        });
      });
    }

    // Shown the moment the query is long enough, before the debounce even starts —
    // otherwise the dropdown sits unchanged for a second and reads as "no posters".
    function injectSkeletons() {
      write(function () {
        results.querySelectorAll('[data-fm-ps-card]').forEach(function (n) {
          n.remove();
        });
        var host = productList();
        skeletonCards('dialog', DIALOG_COUNT).forEach(function (li) {
          host.appendChild(li);
        });
      });
    }

    function schedule() {
      clearTimeout(timer);
      var q = (input.value || '').trim();

      if (q.length < MIN_CHARS) {
        clearCards();
        return;
      }

      var sent = q;
      var mine = ++token;

      if (memo[sent]) {
        inject(q, sent, memo[sent].list);
        return;
      }

      injectSkeletons();
      timer = setTimeout(function () {
        fetchPins(sent, q)
          .then(function (page) {
            if (mine === token) inject(q, sent, page.list);
          })
          .catch(function () {
            // Clear the skeletons, or they shimmer forever on a failed search.
            if (mine === token) clearCards();
          });
      }, TYPING_DELAY);
    }

    input.addEventListener('input', schedule);
    // The reset button and the dialog close clear the value in code, firing no
    // input event — re-read it after any click inside the component.
    comp.addEventListener('click', function () {
      setTimeout(schedule, 0);
    });
    watch();
    schedule();
  }

  /* ───────────────────────────── search page ───────────────────────────── */

  function gridCard(im, title, href) {
    var li = document.createElement('li');
    li.className = 'product-grid__item';
    li.setAttribute('data-fm-ps-card', '');
    li.innerHTML =
      '<a class="fm-pcard" href="' + href + '">' +
      '<span class="fm-pcard__media" data-fm-ps-media><img src="' + framedThumb(im) + '" alt="" loading="lazy"></span>' +
      '<p class="fm-pcard__title">' + title + '</p>' +
      '<p class="fm-pcard__price">' + priceMarkup() + '</p>' +
      '</a>';
    return li;
  }

  // With nothing to show the theme renders a "No products found" block and no
  // <ul> at all, so there is nowhere to put the cards. Build the list it would
  // have rendered and drop that block, which is no longer true.
  function buildGrid() {
    var list = document.querySelector('results-list');
    if (!list) return null;

    var empty = list.querySelector('.main-collection-grid__empty');
    var host = empty ? empty.parentElement : list.querySelector('.collection-wrapper') || list;
    var sectionId = list.getAttribute('section-id') || '';

    var ul = document.createElement('ul');
    // The section-scoped class carries the column definitions — a bare
    // .product-grid only gets the two-column mobile fallback.
    ul.className = 'product-grid' + (sectionId ? ' product-grid--' + sectionId : '') + ' product-grid--grid';
    ul.setAttribute('role', 'list');
    ul.setAttribute('product-grid-view', 'default');

    if (empty) empty.parentElement.insertBefore(ul, empty);
    else host.appendChild(ul);
    if (empty) empty.remove();
    return ul;
  }

  function initGrid() {
    var grid = document.querySelector('ul.product-grid') || buildGrid();
    if (!grid) return;

    var q = '';
    try {
      q = (new URLSearchParams(window.location.search).get('q') || '').trim();
    } catch (e) {
      return;
    }
    // Too short to search. The section stashed the theme's empty state and painted
    // skeletons server-side without knowing the length, so hand the page straight
    // back rather than leaving them shimmering until the 6s safety timeout.
    if (q.length < MIN_CHARS) {
      grid.querySelectorAll('[data-fm-ps-skeleton]').forEach(function (n) {
        n.remove();
      });
      document.querySelectorAll('.fm-ps-stashed').forEach(function (n) {
        n.classList.remove('fm-ps-stashed');
      });
      return;
    }

    var sent = q;
    var observer = null;

    // The store matched nothing, so the page is entirely the theme's empty state:
    // a "no results" line, a heading, and a fallback collection of products that
    // have nothing to do with the search. Once poster results exist, all of that
    // is both wrong and in the way — clear it and let the page read like any other
    // results page. The infinite-scroll sentinels go too, or paginated-list.js
    // scrolls the fallback collection straight back in.
    function emptyStateNodes() {
      var nodes = [];
      document
        .querySelectorAll(
          '.search-results__no-results, .main-collection-grid__title, [ref="viewMoreNext"], [ref="viewMorePrevious"]'
        )
        .forEach(function (n) {
          nodes.push(n);
        });
      grid.querySelectorAll('.product-grid__item:not([data-fm-ps-card])').forEach(function (n) {
        nodes.push(n);
      });
      return nodes;
    }

    // Hidden rather than removed while the search is still in flight: if it fails
    // we put the theme's own page back, and removing is not undoable. Hiding also
    // stops paginated-list.js, since a display:none sentinel never intersects.
    function hideEmptyState() {
      emptyStateNodes().forEach(function (n) {
        n.classList.add('fm-ps-stashed');
      });
    }

    function restoreEmptyState() {
      document.querySelectorAll('.fm-ps-stashed').forEach(function (n) {
        n.classList.remove('fm-ps-stashed');
      });
    }

    function clearEmptyState() {
      emptyStateNodes().forEach(function (n) {
        n.remove();
      });
    }

    // Everything shown so far, plus where the API left off.
    var state = { all: [], shown: 0, bookmark: null, done: false, loading: false, pages: 0 };
    var MAX_PAGES = 10;

    function addPage(page) {
      var seen = {};
      state.all.forEach(function (im) {
        seen[im.id] = true;
      });
      page.list.forEach(function (im) {
        // Pages can overlap; a repeated pin would render as a duplicate card.
        if (!im.id || !seen[im.id]) state.all.push(im);
      });
      state.bookmark = page.bookmark;
      if (!page.bookmark || !page.list.length) state.done = true;
    }

    // Incremental on purpose: existing cards stay where they are and only the new
    // ones are appended. Tearing the grid down and rebuilding it made every <img>
    // repaint (a visible flash on desktop) and collapsed the page height for a
    // frame, which threw the scroll position back to the top on mobile.
    function render() {
      if (observer) observer.disconnect();
      // Skeletons carry the card attribute too — clear them, keep the real cards.
      grid.querySelectorAll('[data-fm-ps-skeleton]').forEach(function (n) {
        n.remove();
      });
      if (SEARCH_COUNT === 0) clearEmptyState();
      var cards = grid.querySelectorAll('[data-fm-ps-card]');
      // The theme's own pagination lands its items after ours — move the existing
      // card nodes back to the end. appendChild moves them without destroying
      // them, so their images never reload.
      var items = grid.querySelectorAll('.product-grid__item');
      if (items.length && !items[items.length - 1].hasAttribute('data-fm-ps-card')) {
        cards.forEach(function (n) {
          grid.appendChild(n);
        });
      }
      for (var i = cards.length; i < Math.min(state.shown, state.all.length); i++) {
        var card = gridCard(state.all[i], q, pinLink(q, sent, state.all[i]));
        grid.appendChild(card);
        shimmerUntilLoaded(card);
      }
      if (observer) observer.observe(grid, { childList: true });
    }

    function showSkeletons(count) {
      if (observer) observer.disconnect();
      grid.querySelectorAll('[data-fm-ps-skeleton]').forEach(function (n) {
        n.remove();
      });
      skeletonCards('grid', count).forEach(function (li) {
        grid.appendChild(li);
      });
      if (observer) observer.observe(grid, { childList: true });
    }

    function loadMore() {
      if (state.loading) return;

      // Reveal what's already buffered before asking for another page.
      if (state.shown < state.all.length) {
        state.shown = Math.min(state.all.length, state.shown + GRID_COUNT);
        render();
        rearm();
        return;
      }

      if (state.done || state.pages >= MAX_PAGES) {
        if (io) io.disconnect();
        return;
      }

      state.loading = true;
      showSkeletons(Math.min(GRID_COUNT, 6));
      searchPage(sent, q, state.bookmark)
        .then(function (page) {
          state.pages++;
          addPage(page);
          state.shown = Math.min(state.all.length, state.shown + GRID_COUNT);
          state.loading = false;
          render();
          rearm();
        })
        .catch(function () {
          // Don't keep hammering a failing endpoint on every scroll.
          state.loading = false;
          state.done = true;
          grid.querySelectorAll('[data-fm-ps-skeleton]').forEach(function (n) {
            n.remove();
          });
          if (io) io.disconnect();
        });
    }

    // The observer only fires on a change of intersection, so a sentinel that is
    // still on screen after loading would never ask for the next page. Re-observing
    // forces a fresh callback when that's the case.
    function rearm() {
      if (!io || state.done) return;
      io.unobserve(sentinel);
      io.observe(sentinel);
    }

    var sentinel = document.createElement('div');
    sentinel.setAttribute('data-fm-ps-sentinel', '');
    sentinel.style.cssText = 'width:100%;height:1px';
    var io = null;

    // Before the first request, not after it. With no matches the page is otherwise
    // 51 unrelated products and a "no results" line for a couple of seconds, then
    // swaps — the flash reads as a broken page.
    if (SEARCH_COUNT === 0) hideEmptyState();
    showSkeletons(GRID_COUNT);

    // Tells the page's own safety timeout that this script is alive and working,
    // so a slow search doesn't get its page yanked back mid-request. Dropped as
    // soon as this settles either way — every outcome below restores or replaces.
    var takeoverFlag = document.documentElement;
    takeoverFlag.setAttribute('data-fm-ps-active', '');
    function takeoverSettled() {
      takeoverFlag.removeAttribute('data-fm-ps-active');
    }

    fetchPins(sent, q)
      .then(function (page) {
        takeoverSettled();
        addPage(page);
        if (!state.all.length) {
          // Nothing to show, so hand the page back to the theme.
          grid.querySelectorAll('[data-fm-ps-skeleton]').forEach(function (n) {
            n.remove();
          });
          restoreEmptyState();
          return;
        }

        state.shown = Math.min(state.all.length, GRID_COUNT);
        render();

        (grid.parentElement || grid).appendChild(sentinel);
        io = new IntersectionObserver(
          function (entries) {
            if (entries[0] && entries[0].isIntersecting) loadMore();
          },
          { rootMargin: '600px 0px' }
        );
        io.observe(sentinel);

        // Filtering, sorting and the theme's own infinite scroll all re-render the
        // grid, which takes the cards with it. Put them back when that happens.
        observer = new MutationObserver(function () {
          var missing = !grid.querySelector('[data-fm-ps-card]');
          // A late page of the fallback collection landing after take-over would
          // otherwise sit alongside the poster cards.
          var foreign = SEARCH_COUNT === 0 && !!grid.querySelector('.product-grid__item:not([data-fm-ps-card])');
          // With real results the theme keeps paginating them, and each new page
          // lands after the poster cards — move them back to the end.
          var items = grid.querySelectorAll('.product-grid__item');
          var trailing = SEARCH_COUNT > 0 && items.length && !items[items.length - 1].hasAttribute('data-fm-ps-card');
          if (missing || foreign || trailing) render();
        });
        observer.observe(grid, { childList: true });
      })
      .catch(function () {
        // Put the theme's own page back rather than leaving skeletons shimmering
        // over a page we emptied.
        takeoverSettled();
        grid.querySelectorAll('[data-fm-ps-skeleton]').forEach(function (n) {
          n.remove();
        });
        restoreEmptyState();
      });
  }

  function initAll() {
    initDialog();
    initGrid();
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);
})();
